import DeveloperModeIcon from "@mui/icons-material/DeveloperMode"
import GroupsIcon from "@mui/icons-material/Groups"
import {
    ButtonBase,
    FormControl,
    Radio,
    RadioGroup,
    Stack,
    Typography,
    useTheme,
} from "@mui/material"
import { ReactElement, useContext } from "react"

import AppButton from "../../buttons/appButton"
import { SolarPanelIcon } from "../../icons/icons"
import ConnectDirectLogo from "../../logos/logos"
import FullScreenTitleContent from "../common/fullScreenTitleContent"
import { AccountSetUpContext } from "../providers/firstLoginProvider"
import {
    EXISTING_DEVICES_CHECK,
    ORG_DETAILS_FORM_INSTALLER,
    EXPECTING_INVITE,
    SOFTWARE_PROVIDER_SETUP,
    FirstLoginDispatcherKeys,
} from "../screenDispatchers/firstLoginConstants"

interface OrgOption {
    title: string
    description: string
    nextScreen: FirstLoginDispatcherKeys
    icon: ReactElement
}

const ICON_FONT_SIZE = "large"
const ORG_OPTIONS: OrgOption[] = [
    {
        title: "Installer",
        description:
            "I am an installer and wish to use the connect direct website to submit LCT applications. Register me and my company",
        nextScreen: ORG_DETAILS_FORM_INSTALLER,
        icon: <GroupsIcon fontSize={ICON_FONT_SIZE} />,
    },
    {
        title: "API Installer or Software Provider",
        description:
            "I am an installer or software provider wishing to access Connect Direct's installer APIs",
        nextScreen: SOFTWARE_PROVIDER_SETUP,
        icon: <DeveloperModeIcon fontSize={ICON_FONT_SIZE} />,
    },
    {
        title: "LCT Manufacturer",
        description:
            "I am a manufacturer of low carbon devices. Set me up as the admin of my organisation",
        nextScreen: EXISTING_DEVICES_CHECK,
        icon: <SolarPanelIcon fontSize={ICON_FONT_SIZE} />,
    },
]

/** Screen where user picks their org type */
const SelectOrgType = (): ReactElement => {
    const theme = useTheme()
    const { setCurrentScreen } = useContext(AccountSetUpContext)

    return (
        <FullScreenTitleContent
            titlePrimaryColor="Welcome"
            titleMain="to ENA Connect Direct"
            subtitle="To help get you set up, please tell us a bit about who you are"
            customSvg={<ConnectDirectLogo fontSize="500px" />}
            content={
                <Stack justifyContent="space-between" height="100%">
                    <Stack spacing={2}>
                        <Stack spacing={1}>
                            <FormControl>
                                <RadioGroup
                                    aria-label="Your plan"
                                    name="people"
                                    defaultValue="Individual"
                                >
                                    {ORG_OPTIONS.map((org) => (
                                        <ButtonBase
                                            key={org.title}
                                            sx={{
                                                minWidth: 240,
                                                width: "100%",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                boxShadow: 1,
                                                border: "1px solid",
                                                borderRadius: 2,
                                                paddingY: 1,
                                                paddingX: 3,
                                                mb: "1rem",
                                                borderColor:
                                                    theme.palette.grey[300],
                                                "&:hover": {
                                                    borderColor:
                                                        theme.palette.primary
                                                            .main,
                                                    boxShadow: 3,
                                                },
                                            }}
                                            onClick={() => {
                                                setCurrentScreen(org.nextScreen)
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={3}
                                            >
                                                {org.icon}
                                                <div>
                                                    <Typography
                                                        align="left"
                                                        color={
                                                            theme.palette.text
                                                                .primary
                                                        }
                                                        variant="h6"
                                                    >
                                                        {org.title}
                                                    </Typography>
                                                    <Typography
                                                        align="left"
                                                        color="text.secondary"
                                                    >
                                                        {org.description}
                                                    </Typography>
                                                </div>
                                            </Stack>

                                            <Radio
                                                sx={{
                                                    display: {
                                                        xs: "none",
                                                        sm: "block",
                                                    },
                                                    flexDirection:
                                                        "row-reverse",
                                                }}
                                            />
                                        </ButtonBase>
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                    </Stack>
                    <Stack>
                        <AppButton
                            sx={{
                                color: theme.palette.text.disabled,
                                fontWeight: 400,
                            }}
                            onClick={() => setCurrentScreen(EXPECTING_INVITE)}
                        >
                            I am expecting an invite
                        </AppButton>
                    </Stack>
                </Stack>
            }
        />
    )
}

export default SelectOrgType
